import Scroller, { ScrollerItem } from "@@/components/Scroller";
import Box from "@@/elements/Box";
import Container from "@@/elements/Container";
import { TokenValue as bps } from "../../../design-tokens/breakpoints";

import { SideHeader } from "@@/components/SideHeader";
import OptimizelyBlock, {
  OptimizelyBlockProps,
} from "@@/integrations/optimizly/Block";
import EpiContent from "@@/integrations/optimizly/Content";
import EpiProperty from "@@/integrations/optimizly/Property";
import { LinkData } from "@@/models/linkdata";
import { useEffect, useState } from "react";
import {
  InfoCard,
  InfoCardProps,
  InfoCardVariant,
} from "../../../components/Card/Info";
import Typography from "../../../elements/Typography";
import styles from "../cardContainer.module.scss";

export type InfoCardContainerProperties = {
  header: string;
  intro?: string;
  label?: string;
  headerVariant?: "left" | "center";
};

export type InfoCardContainerOwnProps = {
  variant: InfoCardVariant;
  cards: InfoCardProps[];
  link?: LinkData;
  sliderDesktop?: boolean;
  sliderMobile?: boolean;
  anchorId?: string;
};

export type InfoCardContainerProps = InfoCardContainerOwnProps &
  InfoCardContainerProperties;

const InfoCardContainer = ({
  header,
  label,
  intro,
  link,
  cards,
  variant,
  sliderDesktop,
  sliderMobile,
  headerVariant = "center",
  anchorId,
}: InfoCardContainerProps) => {
  const [slider, setSlider] = useState<boolean>(false);
  const [shouldShowSideHeader, setShouldShowSideHeader] = useState<boolean>(false);

  useEffect(() => {
    const checkLayout = () => {
      const isSlider = (sliderMobile && window.innerWidth < bps.Large) ||
        (sliderDesktop && variant === "image" && window.innerWidth >= bps.Large);
      setSlider(isSlider);

      // Check if we should show side header
      setShouldShowSideHeader(
        sliderDesktop &&
        variant === "image" &&
        window.innerWidth >= bps.Large &&
        headerVariant === "left",
      );
    };

    checkLayout();

    if (typeof window !== "undefined") {
      window.addEventListener("resize", checkLayout);

      return () => {
        window.removeEventListener("resize", checkLayout);
      };
    }
  }, [sliderDesktop, sliderMobile, headerVariant, variant]);

  if (shouldShowSideHeader) {
    return (
      <Box
        id={anchorId}
        component="section"
        className={styles.block}>
        <SideHeader
          header={header}
          label={label}
          link={link}>
          <Scroller
            fullPageStretch>
            {cards.map((card, idx) => (
              <ScrollerItem
                key={idx}
                className={styles.info_card_scroller_item}>
                <InfoCard
                  variant={variant}
                  body={card.body}
                  header={card.header}
                  color={card.color}
                  link={card.link}
                  image={card.image}
                  fixedWidth
                />
              </ScrollerItem>
            ))}
          </Scroller>
        </SideHeader>
      </Box>
    );
  }

  return (
    <Box id={anchorId} className={styles.block}>
      <Container component="div">
        <Box
          width="full"
          alignItems="center"
          flexDirection="column"
          textAlign="center"
        >
          {headerVariant === "left" && label && (
            <EpiProperty<InfoCardContainerProperties> name="label">
              <div >
                <Typography
                  textcolor="dark"
                  variant="pretitle"
                  asElement="div">
                  {label}
                </Typography>
                <hr />
              </div>
            </EpiProperty>
          )}
          {header && (
            <EpiProperty<InfoCardContainerProperties> name="header">
              <Typography
                mb={intro ? 8 : [10, 12, 14]}
                variant="heading1"
                asElement="h2"
              >
                {header}
              </Typography>
            </EpiProperty>
          )}
          {headerVariant === "center" && intro && (
            <EpiProperty<InfoCardContainerProperties> name="intro">
              <Typography
                mb={[10, 12, 14]}
                variant="intro">
                {intro}
              </Typography>
            </EpiProperty>
          )}
        </Box>
        {!slider && (
          <EpiContent>
            <Box className={variant === "color" && cards.length === 2 ? styles.info_two_grid : styles.info_grid}>
              {cards.map((card, index) => (
                <InfoCard
                  key={index}
                  variant={variant}
                  body={card.body}
                  header={card.header}
                  color={card.color}
                  link={card.link}
                  image={card.image}
                />
              ))}
            </Box>
          </EpiContent>
        )}

        {slider && (
          <EpiContent>
            <Scroller
              fullPageStretch
              fullWidthItemsMobile={false}>
              {cards.map((card, index) => (
                <ScrollerItem
                  key={index}>
                  <InfoCard
                    variant={variant}
                    body={card.body}
                    header={card.header}
                    color={card.color}
                    link={card.link}
                    image={card.image}
                    fixedWidth
                  />
                </ScrollerItem>
              ))}
            </Scroller>
          </EpiContent>
        )}
      </Container>
    </Box>
  );
};

export type ProvidedInfoCardContainerProps =
  OptimizelyBlockProps<InfoCardContainerProperties> & InfoCardContainerProps;

const ProvidedInfoCardContainer = ({
  blockId,
  inEditMode,
  properties,
  anchorId,
  ...rest
}: ProvidedInfoCardContainerProps) => (
  <OptimizelyBlock
    blockId={blockId}
    inEditMode={inEditMode}
    properties={properties}
    anchorId={anchorId}
  >
    {(optimizlyProperties) => (
      <InfoCardContainer
        anchorId={anchorId}
        {...rest}
        {...optimizlyProperties} />
    )}
  </OptimizelyBlock>
);

export default ProvidedInfoCardContainer;
